import { SearchResult, AutocompleteResponse, SearchParams } from './types';
import { HttpApiClient } from './httpApiClient';

type ConnectionState = 'disconnected' | 'connecting' | 'connected';

export class WsApiClient {
  private ws: WebSocket | null = null;
  private messageCallbacks: Map<
    string,
    { resolve: (data: unknown) => void; reject: (err: Error) => void }
  > = new Map();
  private apiKey: string;
  private baseUrl: string;

  private _state: ConnectionState = 'disconnected';
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private baseReconnectDelay = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    baseUrl: string,
    apiKey: string,
    private httpApiClient: HttpApiClient
  ) {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey;
    this.connect();
  }

  get state(): ConnectionState {
    return this._state;
  }

  public isConnected(): boolean {
    return this._state === 'connected';
  }

  private async connect() {
    if (this._state === 'connecting') {
      return; // Already connecting
    }

    this._state = 'connecting';

    try {
      const jwt = await this.httpApiClient.login();
      const wsUrl = `${this.baseUrl}?api_key=${encodeURIComponent(this.apiKey)}&session_id=${jwt}`;
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[WS] Connected');
        this._state = 'connected';
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => {
        try {
          const { id, data } = JSON.parse(event.data);
          const callback = this.messageCallbacks.get(id);
          if (callback) {
            callback.resolve(data);
            this.messageCallbacks.delete(id);
          } else {
            console.warn('[WS] No callback found for message id:', id);
          }
        } catch (error) {
          console.error('[WS] Failed to parse message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WS] Error:', error);
      };

      this.ws.onclose = (event) => {
        console.log(`[WS] Disconnected (code: ${event.code})`);
        this._state = 'disconnected';
        this.rejectPendingRequests(new Error('WebSocket connection closed'));
        this.scheduleReconnect();
      };
    } catch (e) {
      console.error('[WS] Connection initialization failed:', e);
      this._state = 'disconnected';
      this.scheduleReconnect();
    }
  }

  private rejectPendingRequests(error: Error) {
    for (const [id, callback] of this.messageCallbacks) {
      callback.reject(error);
      this.messageCallbacks.delete(id);
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) {
      return; // Already scheduled
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[WS] Max reconnect attempts reached, giving up');
      return;
    }

    this.reconnectAttempts++;
    const delay =
      this.baseReconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1);

    console.log(
      `[WS] Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${Math.round(delay)}ms`
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /** Reset reconnect counter - call this if you want to allow reconnects again */
  public resetReconnect() {
    this.reconnectAttempts = 0;
    if (this._state === 'disconnected') {
      this.connect();
    }
  }

  private sendMessage<T>(
    type: string,
    payload: Record<string, unknown>
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket is not connected'));
        return;
      }

      const id = crypto.randomUUID();
      const timeoutId = setTimeout(() => {
        if (this.messageCallbacks.has(id)) {
          this.messageCallbacks.delete(id);
          reject(new Error('WebSocket request timeout'));
        }
      }, 10000);

      this.messageCallbacks.set(id, {
        resolve: (data) => {
          clearTimeout(timeoutId);
          resolve(data as T);
        },
        reject: (err) => {
          clearTimeout(timeoutId);
          reject(err);
        },
      });

      const message = { id, type, ...payload };
      this.ws.send(JSON.stringify(message));
    });
  }

  public search(searchParams: SearchParams): Promise<SearchResult> {
    const {
      query,
      selectedFacets,
      selectedPriceRange,
      expandedFacets,
      sort,
      booleanFacets,
    } = searchParams;
    const payload: Record<string, unknown> = {
      q: query,
      facets: selectedFacets || {},
      include_facets: expandedFacets || {},
      sort: sort,
      boolean_facets: booleanFacets || null,
    };

    if (selectedPriceRange) {
      payload.min_price = selectedPriceRange[0];
      payload.max_price = selectedPriceRange[1];
    }

    return this.sendMessage('search', payload);
  }

  public autocomplete(query: string): Promise<AutocompleteResponse> {
    return this.sendMessage('autocomplete', { q: query });
  }
}
